import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../lib/api";
import { useCart } from "../context/CartContext";

const COUNTRIES = [
  { code: "GB", label: "United Kingdom" },
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
  { code: "AU", label: "Australia" },
  { code: "IE", label: "Ireland" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
];

const EMPTY_ADDR = { full_name: "", line1: "", line2: "", city: "", postal_code: "", country: "GB" };

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const [loading, setLoading] = useState(false);
  const [addr, setAddr] = useState(() => {
    try {
      return { ...EMPTY_ADDR, ...(JSON.parse(localStorage.getItem("nesta_addr")) || {}) };
    } catch {
      return EMPTY_ADDR;
    }
  });
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    localStorage.setItem("nesta_addr", JSON.stringify(addr));
  }, [addr]);

  const addressComplete =
    addr.full_name.trim() &&
    addr.line1.trim() &&
    addr.city.trim() &&
    addr.postal_code.trim() &&
    addr.country.trim();

  const setField = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));

  const checkout = async () => {
    if (!addressComplete) {
      setTouched(true);
      toast.error("Please enter your delivery address first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, color: i.color, size: i.size })),
        origin_url: window.location.origin,
        shipping: addr,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0)
    return (
      <div data-testid="cart-empty" className="max-w-7xl mx-auto px-6 md:px-10 py-32 text-center">
        <ShoppingBag size={40} className="mx-auto mb-6 text-[#ccc]" />
        <h1 className="font-serif text-4xl mb-4">Your cart is empty</h1>
        <p className="text-[#666] mb-8">Discover pieces designed for modern living.</p>
        <Link
          to="/shop"
          className="inline-block bg-[#1a1a1a] text-white px-8 py-4 text-sm uppercase tracking-luxe hover:bg-[#333] transition-colors"
        >
          Browse Collection
        </Link>
      </div>
    );

  const inputCls =
    "w-full border border-[#ddd] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors";
  const invalidCls = "border-[#b00020]";

  return (
    <div data-testid="cart-page" className="max-w-7xl mx-auto px-6 md:px-10 py-16">
      <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-12">Cart</h1>
      <div className="grid lg:grid-cols-3 gap-16">
        <div className="lg:col-span-2">
          <div className="divide-y divide-[#e5e5e5]">
            {items.map((i) => (
              <div key={i.key} data-testid={`cart-item-${i.product_id}`} className="flex gap-6 py-6">
                <div className="w-28 h-32 bg-[#f5f5f0] overflow-hidden shrink-0">
                  <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-serif text-xl">{i.name}</h3>
                      {(i.color || i.size) && (
                        <div className="text-xs text-[#999] mt-1">
                          {i.color && <span>Colour: {i.color}</span>}
                          {i.color && i.size && <span> · </span>}
                          {i.size && <span>{i.size_label || "Size"}: {i.size}</span>}
                        </div>
                      )}
                    </div>
                    <button
                      data-testid={`remove-${i.product_id}`}
                      onClick={() => removeItem(i.key)}
                      className="text-[#999] hover:text-black"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center border border-[#e5e5e5]">
                      <button onClick={() => updateQty(i.key, i.quantity - 1)} className="p-2 hover:bg-[#f5f5f0]">
                        <Minus size={14} />
                      </button>
                      <span className="w-10 text-center text-sm">{i.quantity}</span>
                      <button onClick={() => updateQty(i.key, i.quantity + 1)} className="p-2 hover:bg-[#f5f5f0]">
                        <Plus size={14} />
                      </button>
                    </div>
                    <div className="font-medium">{money(i.price * i.quantity)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Delivery address */}
          <div data-testid="delivery-address" className="mt-12 border-t border-[#e5e5e5] pt-10">
            <h2 className="font-serif text-2xl mb-6">Delivery Address</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-luxe text-[#999]">Full name</label>
                <input
                  data-testid="addr-full-name"
                  value={addr.full_name}
                  onChange={setField("full_name")}
                  className={`${inputCls} ${touched && !addr.full_name.trim() ? invalidCls : ""}`}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-luxe text-[#999]">Address line 1</label>
                <input
                  data-testid="addr-line1"
                  value={addr.line1}
                  onChange={setField("line1")}
                  className={`${inputCls} ${touched && !addr.line1.trim() ? invalidCls : ""}`}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-luxe text-[#999]">Address line 2 (optional)</label>
                <input data-testid="addr-line2" value={addr.line2} onChange={setField("line2")} className={inputCls} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-luxe text-[#999]">City</label>
                <input
                  data-testid="addr-city"
                  value={addr.city}
                  onChange={setField("city")}
                  className={`${inputCls} ${touched && !addr.city.trim() ? invalidCls : ""}`}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-luxe text-[#999]">Postcode</label>
                <input
                  data-testid="addr-postal-code"
                  value={addr.postal_code}
                  onChange={setField("postal_code")}
                  className={`${inputCls} ${touched && !addr.postal_code.trim() ? invalidCls : ""}`}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-luxe text-[#999]">Country</label>
                <select data-testid="addr-country" value={addr.country} onChange={setField("country")} className={inputCls}>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-[#f5f5f0] p-8 sticky top-28">
            <h2 className="font-serif text-2xl mb-6">Summary</h2>
            <div className="flex justify-between text-sm mb-3">
              <span className="text-[#666]">Subtotal</span>
              <span data-testid="cart-subtotal">{money(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm mb-6 pb-6 border-b border-[#ddd]">
              <span className="text-[#666]">Shipping</span>
              <span>Free</span>
            </div>
            <div className="flex justify-between text-lg mb-8">
              <span>Total</span>
              <span className="font-medium">{money(subtotal)}</span>
            </div>

            {!addressComplete && (
              <p data-testid="address-required-note" className="text-[11px] text-[#b00020] mb-4">
                Enter your delivery address to continue.
              </p>
            )}

            <button
              data-testid="checkout-btn"
              onClick={checkout}
              disabled={loading}
              className="w-full bg-[#1a1a1a] text-white py-4 text-sm uppercase tracking-luxe hover:bg-[#333] transition-colors disabled:opacity-50"
            >
              {loading ? "Redirecting…" : "Pay with Card"}
            </button>
            <p className="text-[11px] text-[#999] text-center mt-4">Secure card payment via Stripe</p>
          </div>
        </div>
      </div>
    </div>
  );
}
